/**
 * UserAvatar - Atom component for displaying user profile pictures with fallback initials
 */
import React from 'react';

interface UserAvatarProps {
  name?: string;
  email?: string;
  picture?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
}

const sizeConfig = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
  xl: 'w-16 h-16 text-xl'
};

const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  email,
  picture,
  size = 'md',
  className = '',
  onClick
}) => {
  const sizeClasses = sizeConfig[size];
  
  // Generate initials from name or email
  const getInitials = (): string => {
    if (name) {
      const names = name.trim().split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    
    if (email) {
      const emailParts = email.split('@');
      if (emailParts.length > 0) {
        return emailParts[0][0].toUpperCase();
      }
    }
    
    return '?';
  };
  
  // Generate consistent color based on name or email
  const getBackgroundColor = (): string => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-cyan-500'
    ];
    
    const text = name || email || 'anonymous';
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };
  
  const initials = getInitials();
  const backgroundColor = getBackgroundColor();
  
  const baseClasses = `${sizeClasses} rounded-full flex items-center justify-center font-medium ${className}`;
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : '';
  
  const displayName = name || email || 'Unknown User';
  
  if (picture) {
    return (
      <img
        src={picture}
        alt={`${displayName}'s profile picture`}
        className={`${baseClasses} ${interactiveClasses} object-cover border-2 border-gray-200 dark:border-gray-600`}
        onClick={onClick}
        onError={(e) => {
          // Fallback to initials if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const fallback = target.nextSibling as HTMLElement;
          if (fallback) {
            fallback.style.display = 'flex';
          }
        }}
      />
    );
  }
  
  return (
    <div
      className={`${baseClasses} ${backgroundColor} text-white ${interactiveClasses}`}
      onClick={onClick}
      title={displayName}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {initials}
    </div>
  );
};

export default UserAvatar; 